import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Descriptions,
  Badge,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { 
  roomService, 
  type Room, 
  type CreateRoomRequest, 
  type UpdateRoomRequest,
  type GetRoomsParams 
} from "../services/roomService";

const { Title } = Typography;
const { Option } = Select;

type RoomStatus = 'available' | 'occupied' | 'maintenance' | 'cleaning';

const RoomsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<GetRoomsParams>({
    page: 1,
    limit: 10,
  });
  const [modal, contextHolder] = Modal.useModal();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Query สำหรับดึงข้อมูลห้อง
  const { data: roomsResponse, isLoading, refetch } = useQuery(
    ['rooms', searchParams],
    () => roomService.getRooms(searchParams),
    {
      keepPreviousData: true,
      onError: (error: any) => {
        message.error('ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຫ້ອງໄດ້');
        console.error('Error fetching rooms:', error);
      }
    }
  );

  // Mutation สำหรับสร้างห้องใหม่
  const createRoomMutation = useMutation(
    (data: CreateRoomRequest) => roomService.createRoom(data),
    {
      onSuccess: () => {
        message.success('ເພີ່ມຫ້ອງສຳເລັດ');
        queryClient.invalidateQueries(['rooms']);
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດເພີ່ມຫ້ອງໄດ້');
      }
    }
  );

  // Mutation สำหรับແກ້ໄຂห้อง
  const updateRoomMutation = useMutation(
    ({ id, data }: { id: string; data: UpdateRoomRequest }) => 
      roomService.updateRoom(id, data),
    {
      onSuccess: () => {
        message.success('ອັບເດດຂໍ້ມູນສຳເລັດ');
        queryClient.invalidateQueries(['rooms']);
        setIsModalVisible(false);
        setEditingRoom(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດແກ້ໄຂຂໍ້ມູນຫ້ອງໄດ້');
      }
    }
  );

  // Mutation สำหรับลบห้อง
  const deleteRoomMutation = useMutation(
    (id: string) => roomService.deleteRoom(id),
    {
      onSuccess: () => {
        message.success('ລຶບຫ້ອງສຳເລັດ');
        queryClient.invalidateQueries(['rooms']);
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດລຶບຫ້ອງໄດ້');
      }
    }
  );

  // Mutation สำหรับเปลี่ยนสถานะห้อง
  const updateStatusMutation = useMutation(
    ({ id, status }: { id: string; status: Room['status'] }) => 
      roomService.updateRoomStatus(id, { status }),
    {
      onSuccess: () => {
        message.success('ປ່ຽນສະຖານະຫ້ອງສຳເລັດ');
        queryClient.invalidateQueries(['rooms']);
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດປ່ຽນສະຖານະໄດ້');
      }
    }
  );

  const handleEdit = (room: Room) => {
    setEditingRoom(room);
    form.setFieldsValue({
      ...room,
      hourlyRate: room.hourlyRate / 100, // แปลงเป็นหน่วยที่แสดงใน UI
    });
    setIsModalVisible(true);
  };

  const handleViewDetails = (room: Room) => {
    setSelectedRoom(room);
    setDetailModalVisible(true);
  };

  const handleSubmit = (values: any) => {
    const formData = {
      ...values,
      hourlyRate: values.hourlyRate * 100, // แปลงกลับเป็น cents/smaller unit
    };

    if (editingRoom) {
      updateRoomMutation.mutate({ id: editingRoom._id, data: formData });
    } else {
      createRoomMutation.mutate(formData);
    }
  };

  const handleStatusChange = (room: Room, newStatus: Room['status']) => {
    modal.confirm({
      title: "ປ່ຽນສະຖານະຫ້ອງ",
      content: `ຕ້ອງການປ່ຽນສະຖານະຫ້ອງ ${room.roomNumber} ເປັນ "${getStatusText(newStatus)}" ຫຼື ບໍ່?`,
      okText: "ຢືນຢັນ",
      cancelText: "ຍົກເລິກ",
      onOk: () => {
        updateStatusMutation.mutate({ id: room._id, status: newStatus });
      },
    });
  };

  const handleDelete = (room: Room) => {
    modal.confirm({
      title: "ຢືນຢັນການລຶບ",
      content: `ຕ້ອງການລຶບ "${room.roomNumber}" ຫຼື ບໍ່?`,
      okText: "ລຶບ",
      okType: "danger",
      cancelText: "ຍົກເລິກ",
      onOk: () => {
        deleteRoomMutation.mutate(room._id);
      },
    });
  };

  const handleSearch = (value: string) => {
    setSearchParams(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleStatusFilter = (status?: string) => {
    setSearchParams(prev => ({ ...prev, status, page: 1 }));
  };

  const handleTableChange = (pagination: any) => {
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const getStatusText = (status: string) => {
    const statusMap = {
      available: "ວ່າງ",
      occupied: "ບໍ່ວ່າງ",
      maintenance: "ປັບປຸງ",
      cleaning: "ອານາໄມ",
    };
    return statusMap[status as keyof typeof statusMap];
  };

  const getStatusColor = (
    status: string
  ): "success" | "error" | "warning" | "processing" | "default" | undefined => {
    const colorMap = {
      available: "success",
      occupied: "error",
      maintenance: "warning",
      cleaning: "processing",
    } as const;

    return colorMap[status as keyof typeof colorMap];
  };

  const columns = [
    {
      title: "ເລກຫ້ອງ",
      dataIndex: "roomNumber",
      key: "roomNumber",
      width: 120,
      sorter: true,
    },
    {
      title: "ຊື່ຫ້ອງ",
      dataIndex: "roomName",
      key: "roomName",
      render: (name: string) => (
        <Space>
          <HomeOutlined style={{ color: "#1890ff" }} />
          {name}
        </Space>
      ),
    },
    {
      title: "ປະເພດ",
      dataIndex: "roomType",
      key: "roomType",
      render: (type: string) => {
        const typeMap = {
          single: { color: "blue", text: "ດ່ຽວ" },
          couple: { color: "green", text: "ຄູ່" },
          vip: { color: "gold", text: "ວີໄອພີ" },
          group: { color: "purple", text: "ກຸ່ມ" },
        };
        const config = typeMap[type as keyof typeof typeMap];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
      filters: [
        { text: 'ດ່ຽວ', value: 'single' },
        { text: 'ຄູ່', value: 'couple' },
        { text: 'ວີໄອພີ', value: 'vip' },
        { text: 'ກຸ່ມ', value: 'group' },
      ],
    },
    {
      title: "ຄວາມຈຸ",
      dataIndex: "capacity",
      key: "capacity",
      render: (capacity: number) => `${capacity} ຄົນ`,
      width: 80,
      sorter: true,
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      render: (status: RoomStatus, record: Room) => (
        <Select
          value={status}
          style={{ width: 120 }}
          loading={updateStatusMutation.isLoading}
          onChange={(newStatus: Room['status']) => handleStatusChange(record, newStatus)}
        >
          <Option value="available">
            <Badge status="success" text="ວ່າງ" />
          </Option>
          <Option value="occupied">
            <Badge status="error" text="ບໍ່ວ່າງ" />
          </Option>
          <Option value="maintenance">
            <Badge status="warning" text="ປັບປຸງ" />
          </Option>
          <Option value="cleaning">
            <Badge status="processing" text="ອານາໄມ" />
          </Option>
        </Select>
      ),
    },
    {
      title: "ອັດຕາ/ຊົ່ວໂມງ",
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      render: (rate: number) => (
        <span style={{ fontWeight: 500, color: "#f56a00" }}>
          {new Intl.NumberFormat("lo-LA").format(rate)} ₭
        </span>
      ),
      sorter: true,
    },
    {
      title: "ຈັດການ",
      key: "actions",
      width: 200,
      render: (_: any, record: Room) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            ເບິ່ງ
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            loading={updateRoomMutation.isLoading}
          >
            ແກ້ໄຂ
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            loading={deleteRoomMutation.isLoading}
          >
            ລຶບ
          </Button>
        </Space>
      ),
    },
  ];

  const rooms = roomsResponse?.data?.rooms || [];
  const pagination = roomsResponse?.data?.pagination;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>ຈັດການຫ້ອງ</Title>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => refetch()}
          loading={isLoading}
        >
          ຮີເຟສ
        </Button>
      </div>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="ຄົ້ນຫາຫ້ອງ..."
              allowClear
              style={{ width: "100%" }}
              onSearch={handleSearch}
              enterButton
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="ເບິ່ງຕາມສະຖານະ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
            >
              <Option value="available">ວ່າງ</Option>
              <Option value="occupied">ບໍ່ວ່າງ</Option>
              <Option value="maintenance">ປັບປຸງ</Option>
              <Option value="cleaning">ອານາໄມ</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              style={{ width: "100%" }}
              loading={createRoomMutation.isLoading}
            >
              ເພີ່ມຫ້ອງໃໝ່
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={rooms}
          rowKey="_id"
          loading={isLoading}
          onChange={handleTableChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: pagination?.pages ? Math.ceil((pagination?.total || 0) / (pagination?.pages || 1)) : 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
          }}
        />
      </Card>
    {contextHolder}
      {/* Add/Edit Room Modal */}
      <Modal
        title={editingRoom ? "ແກ້ໄຂຂໍ້ມູນຫ້ອງ" : "ເພີ່ມຫ້ອງໃໝ່"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingRoom(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
        confirmLoading={createRoomMutation.isLoading || updateRoomMutation.isLoading}
      >
        <Spin spinning={createRoomMutation.isLoading || updateRoomMutation.isLoading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="roomNumber"
                  label="ໝາຍເລກຫ້ອງ"
                  rules={[{ required: true, message: "ກະລຸນາປ້ອນໝາຍເລກຫ້ອງ" }]}
                >
                  <Input placeholder="ໝາຍເລກຫ້ອງ" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="roomName"
                  label="ຊື່ຫ້ອງ"
                  rules={[{ required: true, message: "ກະລຸນາປ້ອນຊື່ຫ້ອງ" }]}
                >
                  <Input placeholder="ຊື່ຫ້ອງ" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="roomType"
                  label="ປະເພດຫ້ອງ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກປະເພດຫ້ອງ" }]}
                >
                  <Select placeholder="ເລືອກປະເພດຫ້ອງ">
                    <Option value="single">ດ່ຽວ</Option>
                    <Option value="couple">ຄູ່</Option>
                    <Option value="vip">ວິໄອພີ</Option>
                    <Option value="group">ກຸ່ມ</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="capacity"
                  label="ຄວາມຈຸ (ຄົນ)"
                  rules={[
                    { required: true, message: "ກະລຸນາປ້ອນຄວາມຈຸ" },
                    // { type: 'number', min: 1, message: "ความจุต้องมากกว่า 0" }
                  ]}
                >
                  <Input type="number" min={1} placeholder="ຄວາມຈຸ" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item 
              name="hourlyRate" 
              label="ອັດຕາຊົ່ວໂມງ (LAK)"
              rules={[
                { required: false, message: "ກະລຸນາປ້ອນອັດຕາຊົ່ວໂມງ" },
                { type: 'number', min: 0, message: "ອັດຕາຕ້ອງຫຼາຍກວ່າ ຫຼື ເທົ່າກັບ 0" }
              ]}
            >
              <Input type="number" min={0} placeholder="ອັດຕາຊົ່ວໂມງ" />
            </Form.Item>

            <Form.Item name="amenities" label="ສິ່ງອຳນວຍຄວາມສຳດວກ">
              <Select mode="multiple" placeholder="ເລືອກສິ່ງອຳນວຍຄວາມສຳດວກ">
                <Option value="air_conditioner">ເຄື່ອງປັບອາກາດ</Option>
                <Option value="sound_system">ລະບົບສຽງ</Option>
                <Option value="jacuzzi">ອ່າງຈາກຸຊີ່</Option>
                <Option value="sauna">ຫ້ອງຊາວນ່າ</Option>
                <Option value="private_bathroom">ຫ້ອງນ້ຳສ່ວນຕົວ</Option>
                <Option value="aromatherapy">ນ້ຳມັນຫອມລະເຫີຍ</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={createRoomMutation.isLoading || updateRoomMutation.isLoading}
                >
                  {editingRoom ? "ອັບເດດ" : "ເພີ່ມ"}
                </Button>
                <Button
                  onClick={() => {
                    setIsModalVisible(false);
                    setEditingRoom(null);
                    form.resetFields();
                  }}
                  disabled={createRoomMutation.isLoading || updateRoomMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Room Detail Modal */}
      <Modal
        title={`ລາຍລະອຽດຫ້ອງ: ${selectedRoom?.roomNumber}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRoom(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ປິດ
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              if (selectedRoom) {
                setDetailModalVisible(false);
                handleEdit(selectedRoom);
              }
            }}
          >
            ແກ້ໄຂ
          </Button>
        ]}
        width={600}
      >
        {selectedRoom && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ເລກຫ້ອງ">
              {selectedRoom.roomNumber}
            </Descriptions.Item>
            <Descriptions.Item label="ຊື່ຫ້ອງ">
              {selectedRoom.roomName}
            </Descriptions.Item>
            <Descriptions.Item label="ປະເພດ">
              <Tag
                color={
                  selectedRoom.roomType === "single"
                    ? "blue"
                    : selectedRoom.roomType === "couple"
                    ? "green"
                    : selectedRoom.roomType === "vip"
                    ? "gold"
                    : "purple"
                }
              >
                {selectedRoom.roomType === "single"
                  ? "ດ່ຽວ"
                  : selectedRoom.roomType === "couple"
                  ? "ຄູ່"
                  : selectedRoom.roomType === "vip"
                  ? "ວີໄອພີ"
                  : "ກຸ່ມ"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="ຄວາມຈຸ">
              {selectedRoom.capacity} ຄົນ
            </Descriptions.Item>
            <Descriptions.Item label="ສະຖານະ">
              <Badge
                status={getStatusColor(selectedRoom.status)}
                text={getStatusText(selectedRoom.status)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="ອັດຕາຕໍ່ຊົ່ວໂມ">
              <span style={{ fontWeight: "bold", color: "#f56a00" }}>
                {new Intl.NumberFormat("lo-LA").format(selectedRoom.hourlyRate)}{" "}
                ₭
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="ວັນທີສ້າງ">
              {new Date(selectedRoom.createdAt).toLocaleDateString('th-TH')}
            </Descriptions.Item>
            <Descriptions.Item label="ອັບເດດລ່າສຸດ">
              {new Date(selectedRoom.updatedAt).toLocaleDateString('th-TH')}
            </Descriptions.Item>
            <Descriptions.Item label="ສິ່ງອຳນວຍຄວາມສະດວກ" span={2}>
              <Space wrap>
                {selectedRoom.amenities?.map((amenity: string) => {
                  const amenityMap = {
                    air_conditioner: "ເຄື່ອງປັບອາກາດ",
                    sound_system: "ລະບົບສຽງ",
                    jacuzzi: "ອ່າງຈາກຸຊີ່", 
                    sauna: "ຫ້ອງຊາວນ່າ",
                    private_bathroom: "ຫ້ອງນ້ຳສ່ວນຕົວ",
                    aromatherapy: "ນ້ຳມັນຫອມລະເຫີຍ",
                  };
                  return (
                    <Tag key={amenity} color="blue">
                      {amenityMap[amenity as keyof typeof amenityMap] ||
                        amenity}
                    </Tag>
                  );
                })}
                {(!selectedRoom.amenities || selectedRoom.amenities.length === 0) && (
                  <span style={{ color: '#999' }}>ບໍ່ມີຂໍ້ມູນ</span>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default RoomsPage;